import React, { useState, useEffect, useRef } from 'react';
import { RotateCcw, Save, X, Clock, Scissors } from 'lucide-react';
import { toast } from 'react-toastify';
import { Clip } from '../types';
import { clipApi, formatDuration } from '../services/api';

interface ClipTimingEditorProps {
  clip: Clip;
  onClipUpdate: (updatedClip: Clip) => void;
  onCancel: () => void;
  onLivePreview?: (liveClip: Clip) => void; // New prop for live preview
}

interface TimelineThumbnail {
  timestamp: number;
  url: string | null;
  index: number;
}

const ClipTimingEditor: React.FC<ClipTimingEditorProps> = ({
  clip,
  onClipUpdate,
  onCancel,
  onLivePreview
}) => {
  const [startTime, setStartTime] = useState(0); // Relative to clip start (0)
  const [endTime, setEndTime] = useState(clip.duration); // Relative to clip start
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDragging, setIsDragging] = useState<'start' | 'end' | null>(null);
  const [thumbnails, setThumbnails] = useState<TimelineThumbnail[]>([]);
  const [thumbnailsLoading, setThumbnailsLoading] = useState(false);
  const [isGeneratingFallback, setIsGeneratingFallback] = useState(false);
  
  const timelineRef = useRef<HTMLDivElement>(null);
  const startHandleRef = useRef<HTMLDivElement>(null);
  const endHandleRef = useRef<HTMLDivElement>(null);

  // Calculate derived values
  const duration = endTime - startTime;
  const clipDuration = clip.duration; // Original clip duration
  const isValid = startTime >= 0 && endTime > startTime && duration >= 1.0 && endTime <= clipDuration;

  // Track changes and provide live preview
  useEffect(() => {
    const changed = startTime !== 0 || endTime !== clip.duration;
    setHasChanges(changed);
    
    // Provide live preview of the edited clip
    if (onLivePreview) {
      const liveClip: Clip = {
        ...clip,
        start_time: clip.start_time + startTime,
        end_time: clip.start_time + endTime,
        duration: endTime - startTime
      };
      onLivePreview(liveClip);
    }
  }, [startTime, endTime, clip, onLivePreview]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      thumbnails.forEach(thumbnail => {
        if (thumbnail.url && thumbnail.url.startsWith('blob:')) {
          URL.revokeObjectURL(thumbnail.url);
        }
      });
    };
  }, [thumbnails]);

  // Load clip timeline thumbnails
  useEffect(() => {
    const loadThumbnails = async () => {
      setThumbnailsLoading(true);
      try {
        const response = await clipApi.getClipTimelineThumbnails(clip.id, 12); // 12 thumbnails for good coverage
        if (response.thumbnails && response.thumbnails.length > 0) {
          setThumbnails(response.thumbnails);
        } else {
          // Fallback: Generate thumbnails from clip video directly
          await generateThumbnailsFromClipVideo();
        }
      } catch (error) {
        console.error('Failed to load clip timeline thumbnails:', error);
        // Fallback: Generate thumbnails from clip video directly
        await generateThumbnailsFromClipVideo();
      } finally {
        setThumbnailsLoading(false);
      }
    };

    const generateThumbnailsFromClipVideo = async () => {
      try {
        setIsGeneratingFallback(true);
        // console.log('🖼️ Generating thumbnails from clip video...');
        
        // Get clip video URL
        const videoUrl = clip.file_url || `/api/clips/${clip.id}/stream`;
        if (!videoUrl) {
          console.warn('No video URL available for thumbnail generation');
          return;
        }

        // Create a hidden video element
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.muted = true;
        video.style.display = 'none';
        document.body.appendChild(video);

        // Create canvas for thumbnail generation
        const canvas = document.createElement('canvas');
        canvas.width = 320;
        canvas.height = 180;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          console.error('Failed to get canvas context');
          return;
        }

        // Load video and generate thumbnails
        const generatedThumbnails: TimelineThumbnail[] = [];
        
        await new Promise<void>((resolve, reject) => {
          video.onloadedmetadata = async () => {
            try {
              const _videoDuration = video.duration;
              const thumbnailCount = 12;
              
              for (let i = 0; i < thumbnailCount; i++) {
                const timestamp = i * (clip.duration / (thumbnailCount - 1));
                
                // Seek to timestamp
                video.currentTime = timestamp;
                
                await new Promise<void>((seekResolve) => {
                  video.onseeked = () => {
                    try {
                      // Draw video frame to canvas
                      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                      
                      // Convert canvas to blob URL
                      canvas.toBlob((blob) => {
                        if (blob) {
                          const thumbnailUrl = URL.createObjectURL(blob);
                          generatedThumbnails.push({
                            timestamp,
                            url: thumbnailUrl,
                            index: i
                          });
                        } else {
                          generatedThumbnails.push({
                            timestamp,
                            url: null,
                            index: i
                          });
                        }
                        seekResolve();
                      }, 'image/jpeg', 0.8);
                    } catch (error) {
                      console.error('Error generating thumbnail:', error);
                      generatedThumbnails.push({
                        timestamp,
                        url: null,
                        index: i
                      });
                      seekResolve();
                    }
                  };
                });
              }
              
              resolve();
            } catch (error) {
              reject(error);
            }
          };
          
          video.onerror = () => {
            reject(new Error('Failed to load video for thumbnail generation'));
          };
          
          video.src = videoUrl;
        });

        // Clean up
        document.body.removeChild(video);
        
        // Set generated thumbnails
        setThumbnails(generatedThumbnails);
        // console.log(`✅ Generated ${generatedThumbnails.length} thumbnails from clip video`);
        
      } catch (error) {
        console.error('Failed to generate thumbnails from clip video:', error);
        // Show empty thumbnails as final fallback
        setThumbnails([]);
      } finally {
        setIsGeneratingFallback(false);
      }
    };

    loadThumbnails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clip.id, clip.file_url]);

  // Time adjustment functions
  const adjustStartTime = (seconds: number) => {
    const newStartTime = Math.max(0, startTime + seconds);
    if (newStartTime < endTime - 1.0) { // Ensure minimum 1 second duration
      setStartTime(newStartTime);
    }
  };

  const adjustEndTime = (seconds: number) => {
    const newEndTime = Math.min(clipDuration, endTime + seconds);
    if (newEndTime > startTime + 1.0) { // Ensure minimum 1 second duration
      setEndTime(newEndTime);
    }
  };

  // Direct input handlers
  const _handleStartTimeChange = (value: string) => {
    const newStartTime = parseFloat(value) || 0;
    if (newStartTime >= 0 && newStartTime < endTime - 1.0) {
      setStartTime(newStartTime);
    }
  };

  const _handleEndTimeChange = (value: string) => {
    const newEndTime = parseFloat(value) || 0;
    if (newEndTime > startTime + 1.0 && newEndTime <= clipDuration) {
      setEndTime(newEndTime);
    }
  };

  // Timeline drag handlers
  const handleTimelineMouseDown = (e: React.MouseEvent, type: 'start' | 'end') => {
    e.preventDefault();
    setIsDragging(type);
  };

  const handleTimelineMouseMove = (e: MouseEvent) => {
    if (!isDragging || !timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(x / rect.width, 1));
    const newTime = percent * clipDuration;

    if (isDragging === 'start') {
      const maxStartTime = endTime - 1.0;
      const clampedStartTime = Math.max(0, Math.min(newTime, maxStartTime));
      setStartTime(clampedStartTime);
    } else if (isDragging === 'end') {
      const minEndTime = startTime + 1.0;
      const clampedEndTime = Math.max(minEndTime, Math.min(newTime, clipDuration));
      setEndTime(clampedEndTime);
    }
  };

  const handleTimelineMouseUp = () => {
    setIsDragging(null);
  };

  // Global mouse event handlers
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleTimelineMouseMove);
      document.addEventListener('mouseup', handleTimelineMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleTimelineMouseMove);
        document.removeEventListener('mouseup', handleTimelineMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, startTime, endTime, clipDuration]);

  // Save changes
  const handleSave = async () => {
    if (!isValid || !hasChanges) return;

    setIsLoading(true);
    try {
      const result = await clipApi.updateClipBoundaries(clip.id, {
        start_time: clip.start_time + startTime,
        end_time: clip.start_time + endTime
      });

      toast.success('Clip timing updated successfully!');
      if (result.note) {
        toast.info(result.note);
      }

      onClipUpdate(result.clip);
    } catch (error: any) {
      console.error('Error updating clip boundaries:', error);
      toast.error(error.response?.data?.detail || 'Failed to update clip timing');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset to original values
  const handleReset = () => {
    setStartTime(0);
    setEndTime(clipDuration);
  };

  // Calculate timeline positions
  const startPercent = (startTime / clipDuration) * 100;
  const endPercent = (endTime / clipDuration) * 100;
  const clipPercent = endPercent - startPercent;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Scissors className="h-5 w-5 text-red-600" />
          <h3 className="text-lg font-semibold text-gray-900">Precision Timing Editor</h3>
        </div>
        <button
          onClick={onCancel}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <X className="h-5 w-5 text-gray-400" />
        </button>
      </div>

      {/* TikTok-style Timeline */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Drag to Adjust Clip Boundaries
        </label>
        
        <div className="relative">
          {/* Timeline container */}
          <div
            ref={timelineRef}
            className="relative w-full h-20 bg-gray-100 rounded-lg cursor-pointer select-none overflow-hidden"
          >
            {/* Thumbnail previews */}
            {thumbnails && thumbnails.length > 0 && (
              <div className="absolute inset-2 flex">
                {thumbnails.map((thumbnail, index) => {
                  const thumbnailWidth = 100 / thumbnails.length; // Equal width distribution
                  return (
                    <div
                      key={index}
                      className="flex-shrink-0 h-full"
                      style={{ 
                        width: `${thumbnailWidth}%`
                      }}
                    >
                      {thumbnail.url ? (
                        <img
                          src={thumbnail.url}
                          alt={`Thumbnail at ${thumbnail.timestamp.toFixed(1)}s`}
                          className="w-full h-full object-cover border-r border-gray-300 last:border-r-0"
                          onError={(e) => {
                            // Show placeholder instead of hiding
                            const target = e.currentTarget;
                            target.style.display = 'none';
                            const placeholder = target.nextElementSibling as HTMLElement;
                            if (placeholder) {
                              placeholder.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <div 
                        className="w-full h-full bg-gray-300 flex items-center justify-center border-r border-gray-300 last:border-r-0"
                        style={{ display: thumbnail.url ? 'none' : 'flex' }}
                      >
                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Fallback: Time markers when no thumbnails */}
            {(!thumbnails || thumbnails.length === 0) && !thumbnailsLoading && (
              <div className="absolute inset-0 flex items-center">
                {[...Array(11)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute top-0 bottom-0 w-px bg-gray-300"
                    style={{ left: `${i * 10}%` }}
                  />
                ))}
              </div>
            )}

            {/* Loading overlay */}
            {thumbnailsLoading && (
              <div className="absolute inset-2 bg-gray-200 rounded flex items-center justify-center">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <div className="text-xs text-gray-600">
                    {isGeneratingFallback 
                      ? 'Generating thumbnails from clip video...'
                      : 'Loading clip timeline previews...'
                    }
                  </div>
                </div>
              </div>
            )}

            {/* Video duration background (when no thumbnails) */}
            {(!thumbnails || thumbnails.length === 0) && !thumbnailsLoading && (
              <div className="absolute inset-2 bg-gray-200 rounded"></div>
            )}

            {/* Selected clip area */}
            <div
              className="absolute top-2 bottom-2 border-2 border-red-500 rounded bg-transparent"
              style={{
                left: `${startPercent}%`,
                width: `${clipPercent}%`,
              }}
            >
              {/* Optional: Add subtle overlay for better visibility */}
              <div className="absolute inset-0 bg-red-500 opacity-10 rounded"></div>
            </div>

            {/* Start handle */}
            <div
              ref={startHandleRef}
              className={`absolute top-0 bottom-0 w-3 bg-white border-2 border-red-500 rounded cursor-ew-resize shadow-lg transform -translate-x-1/2 ${
                isDragging === 'start' ? 'scale-110' : 'hover:scale-105'
              } transition-transform`}
              style={{ left: `${startPercent}%` }}
              onMouseDown={(e) => handleTimelineMouseDown(e, 'start')}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-1 h-8 bg-red-500 rounded-full"></div>
              </div>
            </div>

            {/* End handle */}
            <div
              ref={endHandleRef}
              className={`absolute top-0 bottom-0 w-3 bg-white border-2 border-red-500 rounded cursor-ew-resize shadow-lg transform -translate-x-1/2 ${
                isDragging === 'end' ? 'scale-110' : 'hover:scale-105'
              } transition-transform`}
              style={{ left: `${endPercent}%` }}
              onMouseDown={(e) => handleTimelineMouseDown(e, 'end')}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-1 h-8 bg-red-500 rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Time labels */}
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>0:00</span>
            <span>{formatDuration(clipDuration)}</span>
          </div>
        </div>
      </div>

      {/* Current clip info */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Original:</span>
            <div className="font-medium text-gray-900">
              {formatDuration(clip.start_time)} - {formatDuration(clip.end_time)}
            </div>
            <div className="text-gray-500">({formatDuration(clip.duration)})</div>
          </div>
          <div>
            <span className="text-gray-500">Current:</span>
            <div className="font-medium text-gray-900">
              {formatDuration(startTime)} - {formatDuration(endTime)}
            </div>
            <div className={`${isValid ? 'text-gray-900' : 'text-red-600'}`}>
              ({formatDuration(duration)})
            </div>
          </div>
          <div>
            <span className="text-gray-500">Status:</span>
            <div className={`font-medium ${hasChanges ? 'text-red-600' : 'text-gray-500'}`}>
              {hasChanges ? 'Modified' : 'Unchanged'}
            </div>
            {!isValid && (
              <div className="text-red-600 text-xs">Invalid timing</div>
            )}
          </div>
        </div>
      </div>

      {/* Precision Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Start Time Controls */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Start Time</label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              value={startTime}
              onChange={(e) => setStartTime(Math.max(0, parseFloat(e.target.value) || 0))}
              step="0.1"
              min="0"
              max={endTime - 0.1}
              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
            />
            <span className="text-sm text-gray-500">s</span>
            <div className="flex space-x-1">
              <button
                onClick={() => adjustStartTime(5)}
                className="px-2 py-1 border border-red-500 text-red-500 bg-transparent rounded text-xs hover:bg-red-50"
              >
                +5s
              </button>
              <button
                onClick={() => adjustStartTime(1)}
                className="px-2 py-1 border border-red-500 text-red-500 bg-transparent rounded text-xs hover:bg-red-50"
              >
                +1s
              </button>
              <button
                onClick={() => adjustStartTime(0.1)}
                className="px-2 py-1 border border-red-500 text-red-500 bg-transparent rounded text-xs hover:bg-red-50"
              >
                +0.1s
              </button>
            </div>
          </div>
        </div>

        {/* End Time Controls */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">End Time</label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              value={endTime}
              onChange={(e) => setEndTime(Math.min(clip.duration, parseFloat(e.target.value) || clip.duration))}
              step="0.1"
              min={startTime + 0.1}
              max={clip.duration}
              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
            />
            <span className="text-sm text-gray-500">s</span>
            <div className="flex space-x-1">
              <button
                onClick={() => adjustEndTime(-5)}
                className="px-2 py-1 border border-red-500 text-red-500 bg-transparent rounded text-xs hover:bg-red-50"
              >
                -5s
              </button>
              <button
                onClick={() => adjustEndTime(-1)}
                className="px-2 py-1 border border-red-500 text-red-500 bg-transparent rounded text-xs hover:bg-red-50"
              >
                -1s
              </button>
              <button
                onClick={() => adjustEndTime(-0.1)}
                className="px-2 py-1 border border-red-500 text-red-500 bg-transparent rounded text-xs hover:bg-red-50"
              >
                -0.1s
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="flex items-center space-x-2">
          <button
            onClick={handleReset}
            disabled={!hasChanges}
            className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Reset</span>
          </button>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid || !hasChanges || isLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Validation messages */}
      {!isValid && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2 text-red-700">
            <Clock className="h-4 w-4" />
            <span className="text-sm font-medium">Invalid timing configuration</span>
          </div>
          <ul className="mt-2 text-sm text-red-600 space-y-1">
            {startTime < 0 && <li>• Start time cannot be negative</li>}
            {endTime <= startTime && <li>• End time must be after start time</li>}
            {duration < 1.0 && <li>• Clip duration must be at least 1 second</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ClipTimingEditor; 